import type {
  CreateImport,
  ImportBatch,
  ImportMapping,
  ImportReport,
  ImportStatus,
  JobAccepted,
  Page,
  UploadRequest,
  UploadResponse,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

/** `/admin/v1/imports` (Bolt 9, ADR-0020): batches, mapping, dry-run report, actions. */

const base = adminPath('/imports');

export function useImports(options: { status?: ImportStatus; pageSize?: number } = {}) {
  const { request, keys } = useApiContext();
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  return usePagedList<ImportBatch>({
    queryKey: keys.imports.list({ status: options.status, pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<ImportBatch>>('GET', base, {
        query: { status: options.status, ...pageQuery(cursor, pageSize) },
        signal,
      }),
  });
}

/**
 * `GET /imports/{id}`. Pass `pollMs` while a triggered discover/dry-run/run job might still be
 * in flight (a small import runs inline and is already terminal by the time the caller sees the
 * response, but a larger one queues for the jobs worker) — the caller decides when that is,
 * same as `useJob`.
 */
export function useImport(importId: string | undefined, options: { pollMs?: number | false } = {}) {
  const { request, keys } = useApiContext();
  const pollMs = options.pollMs ?? false;
  return useQuery({
    queryKey: keys.imports.detail(importId ?? ''),
    queryFn: ({ signal }) => request<ImportBatch>('GET', `${base}/${importId}`, { signal }),
    enabled: importId !== undefined,
    refetchInterval: pollMs === false ? false : pollMs,
  });
}

export function useCreateImport() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: CreateImport) => request<ImportBatch>('POST', base, { body: input }),
    onSuccess: () => invalidate(keys.imports.all),
  });
}

export function useImportMapping(importId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.imports.mapping(importId ?? ''),
    queryFn: ({ signal }) =>
      request<ImportMapping>('GET', `${base}/${importId}/mapping`, { signal }),
    enabled: importId !== undefined,
  });
}

/** `PUT /imports/{id}/mapping`; 400 `errors[]` by path (unknown target ids) via `ApiError`. */
export function usePutImportMapping(importId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: ImportMapping) =>
      request<ImportMapping>('PUT', `${base}/${importId}/mapping`, { body: input }),
    onSuccess: () => invalidate(keys.imports.mapping(importId), keys.imports.detail(importId)),
  });
}

export function useImportReport(importId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.imports.report(importId ?? ''),
    queryFn: ({ signal }) => request<ImportReport>('GET', `${base}/${importId}/report`, { signal }),
    enabled: importId !== undefined,
  });
}

/**
 * `POST /imports/{id}/upload`: presigned POST for the CSV file; upload the bytes separately.
 * `importId` is a mutation variable, not a hook argument — the wizard only knows it once
 * `useCreateImport` has already returned (a hook's arguments can't depend on another hook's
 * result within the same render).
 */
export function useUploadImportCsv() {
  const { request } = useApiContext();
  return useMutation({
    mutationFn: (input: UploadRequest & { importId: string }) => {
      const { importId, ...body } = input;
      return request<UploadResponse>('POST', `${base}/${importId}/upload`, { body });
    },
  });
}

type ImportAction = 'discover' | 'dry-run' | 'run';

/** `POST /imports/{id}/discover|dry-run|run`: always 202 `{jobId}` (small imports already ran inline). */
function useImportAction(importId: string, action: ImportAction) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: () => request<JobAccepted>('POST', `${base}/${importId}/${action}`, { body: {} }),
    onSuccess: () => invalidate(keys.imports.detail(importId), keys.imports.report(importId)),
  });
}

export const useDiscoverImport = (importId: string) => useImportAction(importId, 'discover');
export const useDryRunImport = (importId: string) => useImportAction(importId, 'dry-run');

/**
 * `POST /imports/{id}/run`. `completePartially` is the report tab's switch: the only way to set
 * `import_batch.options.completePartially` once the batch already exists (it lifts `/run`'s
 * refusal to start while the last dry run reported blocking errors — it does not itself skip
 * the flagged rows, the source adapter does that).
 */
export function useRunImport(importId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: (input: { completePartially?: boolean } = {}) =>
      request<JobAccepted>('POST', `${base}/${importId}/run`, { body: input }),
    onSuccess: () => invalidate(keys.imports.detail(importId), keys.imports.report(importId)),
  });
}

export function useCancelImport(importId: string) {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: () => request<ImportBatch>('POST', `${base}/${importId}/cancel`, { body: {} }),
    onSuccess: () => invalidate(keys.imports.detail(importId)),
  });
}
